// One lead as a compact, collapsible card — so 50+ leads still fit on a screen.
// Collapsed it reads as an organisation and its contact (one identity block), then
// owner, next step (the earliest open task), stage, and the ⋯ menu. Everything
// ABOUT the lead — changing its stage, editing it, its history — is reachable from
// that collapsed row; expanding is only ever about the WORK: the task list and
// adding a task.
//
// Two colours, two meanings, deliberately kept apart: the card's inline-start edge
// is the STAGE colour, so a run of cards reads as one stage while scrolling; the
// monogram ring is the CLIENT's own colour, identifying the organisation.
//
// The header is a grid, NOT a button: the stage control is a <select> and the select
// checkbox is an <input>, and HTML forbids interactive content inside a button. So the
// expand affordance is its own button covering everything except those two cells, which
// sit beside it. That also stops a stage change — or ticking the lead for deletion —
// from toggling the card open.
import { useState } from 'react';
import { clientsApi } from '../../api';
import he from '../../i18n/he';
import { messageForError } from '../../i18n/errors';
import Button from '../../components/Button';
import TaskList, { openTasks as openOnly } from '../../components/TaskList';
import UrgencyBadge, { topUrgency } from '../../components/UrgencyBadge';
import DateText from '../../components/DateText';
import LeadNotes from './LeadNotes';
import LeadMenu from './LeadMenu';

// Pipeline stages only. 'active' and 'lost' are terminal — they take the lead off
// this screen entirely — so they moved to the ⋯ menu, where each opens a dialog that
// states the consequence first. This dropdown is now strictly for moving a live deal
// along, and every option in it leaves the lead on the screen. Safe to shorten: the
// leads query returns pipeline stages only, so no row can hold a value listed here.
const STAGE_ORDER = ['new', 'contacted', 'meeting', 'proposal', 'negotiation'];

const monogram = (name) => (name || '').trim().charAt(0) || '?';

export default function LeadRow({
  group, onChanged, onEdit, onAdd, onEditLead, onConvert, onMarkLost,
  selected, onToggleSelect,
  editingNoteId, onStartNote, onStopNote,
}) {
  const { client, lead, tasks } = group;
  const [open, setOpen] = useState(false);
  const [stageError, setStageError] = useState(null);

  // Completed tasks now travel with the group, so the collapsed row must read only
  // the OPEN ones: a finished task is neither the next step nor still burning.
  const openTasks = openOnly(tasks);
  const next = openTasks[0]; // server orders open tasks by due date → the next step
  const contact = [client.contact_name, client.phone].filter(Boolean).join(' · ');
  // WHEN TO GET BACK TO THEM — a property of the lead, not an item in its task list.
  // The state is the server's, from the same date rule a task's deadline is read by.
  const followUpState = lead.follow_up_state;
  const followUpDue = followUpState === 'overdue' || followUpState === 'today';

  const advance = async (e) => {
    const to = e.target.value;
    if (to === lead.stage) return;
    setStageError(null);
    try { await clientsApi.advanceStage(client.id, to); onChanged(); }
    catch (err) { setStageError(messageForError(err)); }
  };

  return (
    <div className={`lead-row card${open ? ' open' : ''}${selected ? ' is-picked' : ''}`} data-stage={lead.stage}>
      <div className="lead-head">
        {/* A sibling of the expand button, not a child of it — HTML forbids a control
            inside a button, and ticking a lead must not open its card. Same reason the
            stage <select> sits outside. */}
        <label className="lead-pick" aria-label={`${he.leads.select}: ${client.name}`}>
          <input type="checkbox" checked={!!selected} onChange={onToggleSelect} />
        </label>
        <button type="button" className="lead-toggle" onClick={() => setOpen((o) => !o)}>
          <span className="chevron">{open ? '▾' : '▸'}</span>
          <span className="lead-avatar" style={{ borderColor: client.color || 'var(--border)' }}>
            {monogram(client.name)}
          </span>
          <span className="lead-identity">
            <span className="lead-name">
              {client.name}
              {/* The hottest task on the lead, so nothing burning hides behind a
                  collapsed card. The next step below is the earliest DUE task,
                  which is not necessarily the same one. */}
              <UrgencyBadge value={topUrgency(openTasks)} />
            </span>
            <span className="lead-contact">{contact || he.leads.noContact}</span>
          </span>
          <span className="lead-owner">
            {lead.owner_name ? (
              <>
                <span className="owner-dot">{monogram(lead.owner_name)}</span>
                <span>{lead.owner_name}</span>
              </>
            ) : <span>—</span>}
          </span>
          {/* THE CALL THAT IS OWED, on the collapsed row. It sits beside the next step
              and is deliberately not mixed into it: the next step is work, this is a
              person waiting to hear from us. Shown only once the day has come — a date
              three weeks out is correctly set and is not news. */}
          {followUpDue && (
            <span className={`follow-up-chip follow-up-${followUpState}`}>
              {followUpState === 'overdue' ? he.leads.followUpLate : he.leads.followUpDue}
              <span className="follow-up-date"><DateText value={lead.follow_up_on} /></span>
            </span>
          )}
          {/* An empty next step shows nothing at all — no placeholder label. */}
          <span className="lead-next">
            {next ? (
              <>
                <span className="next-arrow" aria-hidden="true">←</span>
                {/* Whether that step's date has arrived, so a late follow-up cannot hide
                    behind a collapsed card — the same reason the urgency badge is up
                    here. The state is the server's `due_state`; this card judges no
                    dates of its own. */}
                {(next.due_state === 'overdue' || next.due_state === 'today') && (
                  <span
                    className={`next-due next-due-${next.due_state}`}
                    title={next.due_state === 'overdue' ? he.alerts.overdue : he.alerts.dueToday}
                  />
                )}
                <span>{next.note || next.title}</span>
              </>
            ) : null}
          </span>
        </button>
        {/* Last cell, so stages line up as one scannable column — and changing one
            never needs the card opened. */}
        <select
          className="stage-select stage-select-pill"
          value={lead.stage}
          onChange={advance}
          aria-label={he.leads.stage}
        >
          {STAGE_ORDER.map((s) => <option key={s} value={s}>{he.leadStages[s]}</option>)}
        </select>
        {/* Lead-level actions: reachable without expanding. */}
        <LeadMenu
          onEditLead={() => onEditLead(client)}
          onConvert={() => onConvert(client)}
          onMarkLost={() => onMarkLost(client)}
        />
      </div>
      {stageError && <p className="error-text small-note lead-stage-error">{stageError}</p>}

      {/* Expanded, the card holds the two things a lead accumulates, in this order:
          THE WORK (tasks — things to produce, which get completed) and then THE CONTACT
          (the log — conversations that already happened and complete nothing). They are
          adjacent and they are not the same list; keeping them visibly apart is the whole
          point of not having put follow-ups in the tasks table.
          The lead's standing description and its follow-up date are edited on the lead
          form, not here — one value each, so they belong beside the name and the phone. */}
      {open && (
        <div className="lead-body">
          <TaskList
            tasks={tasks}
            emptyText={he.leads.emptyGroupTasks}
            onEdit={onEdit}
            onChanged={onChanged}
            editingNoteId={editingNoteId}
            onStartNote={onStartNote}
            onStopNote={onStopNote}
          />
          <div className="lead-add">
            <Button small variant="ghost" onClick={() => onAdd(client)}>{he.board.addTask}</Button>
          </div>
          {/* Fetched on expand, not with the board — see LeadNotes. */}
          <LeadNotes clientId={client.id} />
        </div>
      )}
    </div>
  );
}
